import config from 'config/api.json';

/**
 * Gets an URL parameter
 * @param location the location
 * @param paramCode the parameter code to retrieve
 * @returns {null|*} the URL parameter value
 */
export function getUrlParameter(location, paramCode) {
  if (!location?.search) {
    return null;
  }
  return new URLSearchParams(location.search).get(paramCode);
}

/**
 * Removes specified query parameters from a given URL.
 *
 * This function takes a location object and an array of parameter names to remove from the URL's query string.
 * It manipulates the URL without reloading the page and returns the new URL as a string.
 *
 * @param {Location} location - The location object, typically from window.location.
 * @param {string[]} paramsToRemove - An array of strings representing the names of the query parameters to remove.
 * @param {string[]} uppercaseParams - An array of strings representing the names of the query parameters to uppercase.
 * @returns {string} The URL as a string with the specified query parameters removed.
 */
export function cleanRelativeUrl(location, paramsToRemove, uppercaseParams = []) {
  const urlParams = new URLSearchParams(location.search);

  paramsToRemove.forEach((param) => {
    urlParams.delete(param);
  });

  uppercaseParams.forEach((param) => {
    if (urlParams.has(param)) {
      const value = urlParams.get(param).toUpperCase();
      urlParams.set(param, value);
    }
  });

  return location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
}

/**
 * Appends a request parameter to a
 * @param args
 * @param key
 * @param value
 * @returns {string|*|{url: (string|*)}}
 */
export function appendQueryStringParam(args, key, value) {
  let urlEnd = typeof args === 'string' ? args : args.url;

  if (urlEnd.indexOf('?') < 0) urlEnd += '?';
  else urlEnd += '&';

  urlEnd += `${key}=${value}`;

  return typeof args === 'string' ? urlEnd : { ...args, url: urlEnd };
}

/**
 * Gets the server base URL.
 */
export function getServerBaseUrl() {
  if (!DEV) {
    if (window.serverBaseUrl) {
      return cleanUrl(window.serverBaseUrl);
    }
    return window.location.origin;
  }
  return cleanUrl(config.devServerBaseUrl);
}

export function getViewerPath() {
  return `${getServerBaseUrl()}/public/web/pdf/viewer.html`;
}

export function buildBlankFileViewerUrl(searchContent) {
  if (searchContent) {
    return `${getViewerPath()}?file=#search=${encodeURIComponent(searchContent)}`;
  }
  return `${getViewerPath()}?file=`;
}

export function getAnonymizationCssPath() {
  return `${getServerBaseUrl()}/arcade/css/anonymization.css`;
}

export function getPSPDFKitPath() {
  return `${getServerBaseUrl()}/arcade/`;
}

// export function buildFileViewerUrl(contentId, searchContent) {
//   if (searchContent)
//     return `${getViewerPath()}?file=${encodeURIComponent(
//         buildArcadeFileContentUrl(contentId)
//     )}#search=${encodeURIComponent(searchContent)}`;
//   return `${getViewerPath()}?file=${encodeURIComponent(buildArcadeFileContentUrl(contentId))}`;
// }

export function buildArcadeFileContentUrl(contentId) {
  return `${getServerBaseUrl()}/arcade/api/entities/content/${contentId}`;
}

export function buildPublicationCertificationUrl(documentId) {
  return `${getServerBaseUrl()}/public/api/generation/${documentId}/publicationCertificate`;
}

/**
 * Gets the page URL.
 * @param pageCode the page code
 * @param appCode the app code
 * @returns {string}
 */
export function getPageURL(pageCode, appCode) {
  const appCodeParam = appCode ? `&app=${appCode}` : '';
  return cleanUrl(`${getServerBaseUrl()}/arcade/page?pageCode=${pageCode}${appCodeParam}`);
}

/**
 * Gets the image URL.
 * @param imageCode the image code
 * @param appCode the app code
 * @returns {string}
 */
export function getImageURL(imageCode, appCode) {
  const appCodeParam = appCode ? `&app=${appCode}` : '';
  return cleanUrl(`${getServerBaseUrl()}/arcade/image?imageCode=${imageCode}${appCodeParam}`);
}

/**
 * Gets the background image URL.
 * @param imageCode the image code
 * @returns {string}
 */
export function getBgImageURL(imageCode) {
  return cleanUrl(`${getServerBaseUrl()}/arcade/bgImage?imageCode=${imageCode}`);
}

export function cleanUrl(url) {
  return url.replace(/([^:]\/)\/+/g, '$1');
}

/**
 * Adds the sort URL parameter.
 * @param urlSearchParams the URLSearchParameter
 * @param sort the sort value
 */
export function addSortParameter(urlSearchParams, sort) {
  if (typeof sort !== 'string') {
    sort.forEach(({ propertyTypeCode, direction }) => {
      urlSearchParams.append('sort', `${propertyTypeCode},${direction}`);
    });
  } else {
    urlSearchParams.append('sort', sort);
  }
}
